/**
 * To fetch and display the live Fortum footer data locally on http://localhost:3000,
 * you need to bypass CORS restrictions using a local proxy.
 *
 * Follow these steps:
 *
 * 1. Open a terminal window.
 * 2. Run the following command:
 *
 *    npx local-cors-proxy --proxyUrl https://www.fortum.com
 *
 * 3. The proxy will start on http://localhost:8010
 *
 * 4. Use the following endpoints in your code to fetch the footer:
 *
 *    - Finnish:  http://localhost:8010/proxy/fi/api/fi/menu/footer
 *    - Swedish:  http://localhost:8010/proxy/fi/api/sv/menu/footer
 *    - English:  http://localhost:8010/proxy/fi/api/en/menu/footer
 *
 * Note: Do not commit these proxy URLs to production. This setup is for local development only.
 */

import React from 'react';
import {
  Box,
  Col,
  ContentText,
  Gap,
  Grid,
  GridContext,
  Heading,
  Hidden,
  Link,
  Row,
  Accordion,
  colors,
  spacing,
  useTheme,
  IconFacebook,
  IconYoutube,
  IconLinkedin,
  breakpoints,
} from '@fortum/elemental-ui';
import { useTranslation } from 'react-i18next';
import { FooterContent } from '@configs/footer';
import { useResponsiveWidth } from '@utils/useResponsiveWidth';
import { Namespace } from '@configs/i18n';
import { StyledLogoLink } from '@components/AppHeader/styles';

interface FooterTemplateProps {
  data?: FooterContent;
  language: string;
}

const footerBorderStyle = `1px solid ${colors.silverGrey}`;
const breakpointOffsets = {
  s: 0,
  m: 160,
  l: 260,
};

export const FooterTemplate: React.FC<FooterTemplateProps> = ({
  data,
  language,
}) => {
  const theme = useTheme();
  const responsiveWidth = useResponsiveWidth();
  const isSmallScreen = responsiveWidth > breakpoints.s;

  const gridContextValue = React.useMemo(
    () => ({ offsets: breakpointOffsets }),
    [],
  );

  const { t: glossaryT } = useTranslation<Namespace>('glossary');

  return (
    <GridContext.Provider value={gridContextValue}>
      <Box backgroundColor={theme.tokens.background.secondary} width="100%">
        <Grid>
          <Row mt={{ s: spacing.s }}>
            {data?.sections?.map(section => (
              <Col key={section.key} s={12} m={8} l={6} xl={4} xxl={3}>
                <Hidden below="s">
                  <StyledLogoLink
                    href={section.absolute}
                    color={colors.inkGrey}
                  >
                    <Heading
                      scale="small"
                      level={2}
                      contentHeading
                      color={colors.inkGrey}
                      mv={spacing.xxs}
                    >
                      {section.title}
                    </Heading>
                  </StyledLogoLink>
                  <Gap size={spacing.xxs} flexDirection="column">
                    {section.below?.map(link => (
                      <Link
                        key={link.key}
                        href={link.absolute}
                        noUnderline
                        color={colors.inkGrey}
                      >
                        {link.title}
                      </Link>
                    ))}
                  </Gap>
                </Hidden>
                <Hidden above="s" borderBottom={footerBorderStyle}>
                  <Accordion
                    openText={
                      <Heading
                        scale="small"
                        level={2}
                        contentHeading
                        color={colors.inkGrey}
                      >
                        {section.title}
                      </Heading>
                    }
                    status="plain"
                    size="l"
                    mv={spacing.xxxxs}
                  >
                    <Gap size={spacing.xxs} flexDirection="column">
                      <Link
                        href={section.absolute}
                        noUnderline
                        color={colors.inkGrey}
                      >
                        {section.title}
                      </Link>
                      {section.below?.map(link => (
                        <Link
                          key={link.key}
                          href={link.absolute}
                          noUnderline
                          color={colors.inkGrey}
                        >
                          {link.title}
                        </Link>
                      ))}
                    </Gap>
                  </Accordion>
                </Hidden>
              </Col>
            ))}
          </Row>

          <Row mt={{ default: spacing.s }}>
            <Col
              xxs={12}
              display="flex"
              flexDirection="column"
              alignItems={!isSmallScreen ? 'center' : 'flex-end'}
            >
              {data?.followUsText && (
                <ContentText
                  mt={{ default: spacing.xs, m: 0 }}
                  mb={spacing.xxs}
                >
                  {data.followUsText}
                </ContentText>
              )}
              <Gap
                display="flex"
                size={spacing.xxs}
                justifyContent={!isSmallScreen ? 'center' : 'flex-end'}
              >
                {data?.socialLinks?.map(link => (
                  <StyledLogoLink
                    key={link.icon}
                    href={link.href}
                    color={colors.inkGrey}
                    aria-label={glossaryT(
                      `ariaLabels.socialMediaLinks.${link.icon}`,
                    )}
                  >
                    {link.icon === 'facebook' && (
                      <IconFacebook
                        aria-hidden
                        size={48}
                        color={colors.inkGrey}
                      />
                    )}
                    {link.icon === 'youtube' && (
                      <IconYoutube
                        aria-hidden
                        size={48}
                        color={colors.inkGrey}
                      />
                    )}
                    {link.icon === 'linkedin' && (
                      <IconLinkedin
                        aria-hidden
                        size={48}
                        color={colors.inkGrey}
                      />
                    )}
                  </StyledLogoLink>
                ))}
              </Gap>
            </Col>
          </Row>

          <Row
            display="flex"
            flexDirection={{ default: 'column', l: 'row-reverse' }}
            mt={{ default: spacing.s }}
          >
            <Col xxs={12} l={8}>
              <Gap
                size={spacing.xxs}
                display="flex"
                flexDirection={{ default: 'column', l: 'row' }}
                justifyContent={{ default: 'center', l: 'flex-end' }}
                alignItems="center"
              >
                {data?.legalLinks?.map(link => (
                  <Link key={link.href} href={link.href} color={colors.inkGrey}>
                    {link.title}
                  </Link>
                ))}
              </Gap>
            </Col>
            <Col xxs={12} l={4}>
              <Box
                mt={{ default: spacing.xs, l: 0 }}
                display="flex"
                justifyContent={{ default: 'center', l: 'flex-start' }}
              >
                <ContentText color={colors.inkGrey} mb={spacing.s}>
                  {data?.copyright}
                </ContentText>
              </Box>
            </Col>
          </Row>
        </Grid>
      </Box>
    </GridContext.Provider>
  );
};
